import api from './api.service';
import type { RejectionPayload } from './rejection.service';

export interface DeliveryItemEligible {
  id: string;
  delivery_note_id: string;
  sales_order_item_id: string;
  line_number: number;
  item_code?: string;
  item_description?: string;
  revision?: string | null;
  delivered_quantity: number;
  invoiced_quantity?: number;
  available_quantity?: number;
  /** Net + VAT for available qty (same basis as create-from-deliveries). */
  invoiceable_amount?: number;
  invoiceable_net_amount?: number;
  invoiceable_vat_amount?: number;
  uom?: string;
  salesOrderItem?: { unit_price?: number; vat_percentage?: number; vat_applicable?: boolean; item_code?: string; item_description?: string };
}

export interface DeliveryEligibleForInvoice {
  id: string;
  delivery_note_number: string;
  delivery_date: string;
  customer_id?: string;
  sales_order_id?: string;
  /** Sum of line invoiceable_amount (incl. VAT). */
  invoiceable_amount?: number;
  salesOrder?: { sales_order_number?: string; department_id?: string | null; currency?: string | null };
  items?: DeliveryItemEligible[];
}

export interface InvoiceDeliveryNoteRef {
  id: string;
  delivery_note_number: string;
}

export interface SalesInvoiceItem {
  id?: string;
  sales_invoice_id?: string;
  sales_order_item_id?: string;
  delivery_note_item_id?: string;
  line_number: number;
  item_code?: string;
  item_description?: string;
  revision?: string | null;
  quantity: number;
  uom?: string;
  unit_price: number;
  line_total?: number;
  discount_amount?: number;
  net_amount?: number;
  vat_applicable?: boolean;
  vat_percentage?: number;
  vat_amount?: number;
  project_id?: string;
  cost_center_id?: string;
  revenue_account_id?: string;
  notes?: string;
}

export interface OpenAdvanceRow {
  /** receipt_voucher: finance receipt; advance_invoice: posted Advance Sales Invoice */
  source: 'receipt_voucher' | 'advance_invoice';
  id: string;
  receipt_number: string;
  receipt_date: string;
  amount: number;
  adjusted_amount: number;
  open_balance: number;
  narration?: string | null;
  payment_mode?: string;
}

/** Payload for POST .../apply-advances (one id type per row). */
export type ApplyAdvanceAllocationInput =
  | { receipt_voucher_id: string; amount: number }
  | { advance_sales_invoice_id: string; amount: number };

export interface OpenCreditNoteRow {
  id: string;
  credit_note_number: string;
  credit_note_date: string;
  total_amount: number;
  adjusted_amount: number;
  open_balance: number;
  reason?: string | null;
}

export interface AdvanceAllocationRow {
  id: string;
  receipt_voucher_id?: string | null;
  advance_sales_invoice_id?: string | null;
  sales_invoice_id: string;
  allocated_amount: number;
  adjustment_date?: string;
  receiptVoucher?: { receipt_number?: string; receipt_date?: string };
  advanceSalesInvoice?: { invoice_number?: string; invoice_date?: string };
}

export interface CreditNoteAllocationRow {
  id: string;
  customer_credit_note_id: string;
  sales_invoice_id: string;
  allocated_amount: number;
  adjustment_date?: string;
  customerCreditNote?: { credit_note_number?: string; credit_note_date?: string };
}

export interface SalesInvoice {
  id: string;
  company_id: string;
  invoice_number: string;
  sales_order_id?: string;
  delivery_note_id?: string;
  customer_po_number?: string | null;
  customer_po_date?: string | null;
  sap_number?: string | null;
  revenue_service_type_id?: string | null;
  customer_id?: string;
  customer_name?: string;
  customer_address?: string;
  /** Ship-to snapshot on the invoice */
  shipping_address?: string | null;
  invoice_date: string;
  due_date?: string;
  payment_terms?: string;
  invoice_type?: string;
  project_id?: string;
  department_id?: string | null;
  subtotal?: number;
  discount_amount?: number;
  total_after_discount?: number;
  vat_amount?: number;
  grand_total?: number;
  /** ISO currency code (e.g. AED); used for display */
  currency?: string;
  balance_due?: number;
  paid_amount?: number;
  advance_adjusted_amount?: number;
  credit_note_adjusted_amount?: number;
  invoice_status: string;
  payment_status?: string;
  approval_status?: string;
  approval_instance_id?: string | null;
  approved_by?: string;
  approved_date?: string;
  is_posted?: boolean;
  posted_date?: string;
  gl_posted?: boolean;
  ar_account_id?: string;
  vat_account_id?: string;
  /** Debited on Advance invoice GL posting; receipt against advance credits this account unless overridden on allocation. */
  advance_from_customer_account_id?: string;
  prepaid_customer_advance_account_id?: string;
  advance_adjustment_account_id?: string;
  retention_account_id?: string;
  is_e_invoice?: boolean;
  e_invoice_uuid?: string;
  e_invoice_hash?: string;
  qr_code_data?: string;
  zatca_status?: string;
  zatca_clearance_status?: string;
  customer_tax_number?: string;
  customer?: {
    id: string;
    name?: string;
    customer_name?: string;
    tax_registration_number?: string;
    vat_number?: string;
    billing_address_line1?: string;
    billing_address_line2?: string;
    billing_city?: string;
    billing_state?: string;
    billing_country?: string;
    billing_postal_code?: string;
    shipping_address_line1?: string;
    shipping_address_line2?: string;
    shipping_city?: string;
    shipping_state?: string;
    shipping_country?: string;
    shipping_postal_code?: string;
  };
  salesOrder?: { id: string; sales_order_number?: string; customer_po_number?: string; customer_po_date?: string; sap_number?: string | null };
  project?: { id: string; name?: string };
  /** Linked delivery notes (from invoice lines and/or header). */
  delivery_notes?: InvoiceDeliveryNoteRef[];
  delivery_note_numbers?: string[];
  delivery_note_number?: string;
}

/** GL posting accounts that can be set anytime before Post to GL. */
export interface SalesInvoiceGlAccounts {
  ar_account_id?: string | null;
  vat_account_id?: string | null;
  advance_adjustment_account_id?: string | null;
  retention_account_id?: string | null;
  prepaid_customer_advance_account_id?: string | null;
  advance_from_customer_account_id?: string | null;
}

class SalesInvoiceService {
  async getAll(filters?: {
    invoice_status?: string;
    payment_status?: string;
    customer_id?: string;
    project_id?: string;
    date_from?: string;
    date_to?: string;
    search?: string;
    invoice_type?: string;
  }): Promise<SalesInvoice[]> {
    const params = new URLSearchParams();
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value != null && value !== '') params.append(key, String(value));
      });
    }
    const response = await api.get<SalesInvoice[]>(`/sales/invoices?${params.toString()}`);
    return (response as any)?.data ?? response;
  }

  async getById(id: string): Promise<SalesInvoice> {
    const response = await api.get<SalesInvoice>(`/sales/invoices/${id}`);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  async getItems(id: string): Promise<SalesInvoiceItem[]> {
    const response = await api.get<SalesInvoiceItem[]>(`/sales/invoices/${id}/items`);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw ?? [];
  }

  async create(invoice: Partial<SalesInvoice>, items: Partial<SalesInvoiceItem>[]): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>('/sales/invoices', { invoice, items });
    return (response as any)?.data ?? response;
  }

  async createFromSalesOrder(sales_order_id: string): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>('/sales/invoices/from-sales-order', { sales_order_id });
    return (response as any)?.data ?? response;
  }

  /** Soft-delete a draft only; frees delivery lines and reverts SO invoiced quantities. */
  async deleteDraft(id: string): Promise<void> {
    await api.delete(`/sales/invoices/${id}/draft`);
  }

  async getDeliveriesEligibleForInvoice(
    customerId: string,
    salesOrderId?: string
  ): Promise<DeliveryEligibleForInvoice[]> {
    const params: Record<string, string> = { customer_id: customerId };
    if (salesOrderId) params.sales_order_id = salesOrderId;
    const response = await api.get<any>('/sales/deliveries/eligible-for-invoice', { params });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw ?? [];
  }

  async createInvoiceFromDeliveries(
    customerId: string,
    deliveryNoteIds: string[],
    departmentId?: string
  ): Promise<SalesInvoice> {
    const payload: {
      customer_id: string;
      delivery_note_ids: string[];
      department_id?: string;
    } = {
      customer_id: customerId,
      delivery_note_ids: deliveryNoteIds,
    };
    if (departmentId?.trim()) payload.department_id = departmentId.trim();
    const response = await api.post<SalesInvoice>('/sales/invoices/from-deliveries', payload);
    return (response as any)?.data ?? response;
  }

  async update(
    id: string,
    invoice: Partial<SalesInvoice>,
    items: Partial<SalesInvoiceItem>[]
  ): Promise<SalesInvoice> {
    const response = await api.put<SalesInvoice>(`/sales/invoices/${id}`, { invoice, items });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  /** Update billing/shipping anytime (except Cancelled); writes back to Customer Master. */
  async updateAddresses(
    id: string,
    addresses: { customer_address?: string | null; shipping_address?: string | null }
  ): Promise<SalesInvoice> {
    const response = await api.patch<SalesInvoice>(`/sales/invoices/${id}/addresses`, addresses);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  /** Set GL posting accounts anytime before GL post (not only Draft). */
  async updateGlAccounts(id: string, accounts: SalesInvoiceGlAccounts): Promise<SalesInvoice> {
    const response = await api.patch<SalesInvoice>(`/sales/invoices/${id}/gl-accounts`, accounts);
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  async submitForApproval(id: string): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>(`/sales/invoices/${id}/submit-for-approval`, {});
    return (response as any)?.data ?? response;
  }

  async approve(id: string, remarks?: string): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>(`/sales/invoices/${id}/approve`, { remarks: remarks ?? undefined });
    return (response as any)?.data ?? response;
  }

  async rejectApproval(id: string, payload: RejectionPayload): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>(`/sales/invoices/${id}/reject-approval`, payload);
    return (response as any)?.data ?? response;
  }

  async revokeApproval(id: string, reason: string): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>(`/sales/invoices/${id}/revoke-approval`, { reason });
    return (response as any)?.data ?? response;
  }

  async post(id: string): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>(`/sales/invoices/${id}/post`);
    return (response as any)?.data ?? response;
  }

  async postToGL(id: string, accounts?: SalesInvoiceGlAccounts): Promise<void> {
    await api.post(`/sales/invoices/${id}/post-to-gl`, accounts ?? {});
  }

  async unpostFromGL(id: string): Promise<void> {
    await api.post(`/sales/invoices/${id}/unpost-gl`);
  }

  async recordPayment(id: string, payment_amount: number): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>(`/sales/invoices/${id}/record-payment`, {
      payment_amount,
    });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw;
  }

  async cancel(id: string): Promise<SalesInvoice> {
    const response = await api.post<SalesInvoice>(`/sales/invoices/${id}/cancel`);
    return (response as any)?.data ?? response;
  }

  async getOutstandingByCustomer(customerId: string, invoiceType?: 'Standard' | 'Advance'): Promise<SalesInvoice[]> {
    const params: { customer_id: string; invoice_type?: string } = { customer_id: customerId };
    if (invoiceType) params.invoice_type = invoiceType;
    const response = await api.get<SalesInvoice[]>('/sales/invoices/outstanding-by-customer', {
      params,
    });
    const data = (response as any)?.data ?? response;
    return Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
  }

  async getOpenAdvances(customerId: string): Promise<OpenAdvanceRow[]> {
    const response = await api.get<any>('/sales/invoices/open-advances', {
      params: { customer_id: customerId },
    });
    const raw = (response as any)?.data ?? response;
    const list = Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
    return list.map((row: any): OpenAdvanceRow => ({
      ...row,
      source: row?.source === 'advance_invoice' ? 'advance_invoice' : 'receipt_voucher',
    }));
  }

  async getOpenCreditNotes(customerId: string): Promise<OpenCreditNoteRow[]> {
    const response = await api.get<any>('/sales/invoices/open-credit-notes', {
      params: { customer_id: customerId },
    });
    const raw = (response as any)?.data ?? response;
    return Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
  }

  async applyAdvances(
    invoiceId: string,
    allocations: ApplyAdvanceAllocationInput[]
  ): Promise<{ invoice: SalesInvoice; allocations: AdvanceAllocationRow[] }> {
    const response = await api.post<any>(`/sales/invoices/${invoiceId}/apply-advances`, {
      allocations,
    });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw ?? { invoice: {} as SalesInvoice, allocations: [] };
  }

  async applyCreditNotes(
    invoiceId: string,
    allocations: { customer_credit_note_id: string; amount: number }[]
  ): Promise<{ invoice: SalesInvoice; allocations: CreditNoteAllocationRow[] }> {
    const response = await api.post<any>(`/sales/invoices/${invoiceId}/apply-credit-notes`, {
      allocations,
    });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw ?? { invoice: {} as SalesInvoice, allocations: [] };
  }

  async getAdvanceAllocations(invoiceId: string): Promise<AdvanceAllocationRow[]> {
    const response = await api.get<any>(`/sales/invoices/${invoiceId}/advance-allocations`);
    const raw = (response as any)?.data ?? response;
    return Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
  }

  async getCreditNoteAllocations(invoiceId: string): Promise<CreditNoteAllocationRow[]> {
    const response = await api.get<any>(`/sales/invoices/${invoiceId}/credit-note-allocations`);
    const raw = (response as any)?.data ?? response;
    return Array.isArray(raw?.data) ? raw.data : Array.isArray(raw) ? raw : [];
  }

  async getVoucherBySalesInvoiceId(invoiceId: string): Promise<VoucherPrintData | null> {
    const response = await api.get<any>('/finance/vouchers/by-reference', {
      params: { reference_type: 'SalesInvoice', reference_id: invoiceId },
    });
    const data = (response as any)?.data ?? response;
    return data ?? null;
  }
}

export interface VoucherDetailPrint {
  id: string;
  account_id: string;
  debit_amount: number;
  credit_amount: number;
  description?: string;
  account?: { account_code: string; account_name: string };
}

export interface VoucherPrintData {
  id: string;
  voucher_number: string;
  voucher_date: string;
  voucher_type: string;
  reference_number?: string;
  description?: string;
  status: string;
  total_debit: number;
  total_credit: number;
  details?: VoucherDetailPrint[];
}

export const chartOfAccountsService = {
  async getIncomeAccounts(isGroup?: boolean): Promise<{ id: string; account_code: string; account_name: string }[]> {
    const params: Record<string, string> = { account_type: 'Income' };
    if (isGroup !== undefined) params.is_group = String(isGroup);
    const response = await api.get<any>('/finance/chart-of-accounts', { params });
    const raw = (response as any)?.data ?? response;
    const list = Array.isArray(raw) ? raw : Array.isArray((response as any)?.data?.data) ? (response as any).data.data : [];
    return list.map((a: any) => ({
      id: a.id,
      account_code: a.account_code ?? '',
      account_name: a.account_name ?? '',
    }));
  },
  async getAccountsByType(accountType: string, isGroup?: boolean): Promise<{ id: string; account_code: string; account_name: string }[]> {
    const params: Record<string, string> = { account_type: accountType };
    if (isGroup !== undefined) params.is_group = String(isGroup);
    const response = await api.get<any>('/finance/chart-of-accounts', { params });
    const raw = (response as any)?.data ?? response;
    const list = Array.isArray(raw) ? raw : Array.isArray((response as any)?.data?.data) ? (response as any).data.data : [];
    return list.map((a: any) => ({
      id: a.id,
      account_code: a.account_code ?? '',
      account_name: a.account_name ?? '',
    }));
  },
  async getAll(params?: { account_type?: string }): Promise<any[]> {
    const response = await api.get<any>('/finance/chart-of-accounts', { params });
    const raw = (response as any)?.data ?? response;
    return raw?.data ?? raw ?? [];
  },
};

const salesInvoiceService = new SalesInvoiceService();
export default salesInvoiceService;
